import { Injectable, inject } from '@angular/core';
import { filter, take } from 'rxjs';
import { DialogService } from 'nebula-ui-kit';
import { AuthService } from './auth.service';
import { VAULT_CONFIG } from './vault-config.token';
import { VaultAppToastService } from './vault-app-toast.service';
import {
  readVaultSessionInactivityMinutes,
  VAULT_SECURITY_SETTINGS_UPDATED_EVENT,
} from './vault-security-settings.util';
import { captureVaultSessionForExpiryLogout } from './vault-post-login-redirect.util';

const DEFAULT_INACTIVITY_MINUTES = 24 * 60;
const DEFAULT_WARNING_MINUTES = 5;
const UPLOAD_ACTIVE_TIMEOUT_MS = 360 * 60 * 1000;
const UPLOAD_ACTIVE_KEY = 'upload_active';

@Injectable({ providedIn: 'root' })
export class VaultSessionInactivityService {
  private readonly auth = inject(AuthService);
  private readonly vaultAppToast = inject(VaultAppToastService);
  private readonly config = inject(VAULT_CONFIG);
  private readonly dialogService = inject(DialogService);

  private logoutTimeout: ReturnType<typeof setTimeout> | null = null;
  private warningTimeout: ReturnType<typeof setTimeout> | null = null;
  private uploadCheckInterval: ReturnType<typeof setInterval> | null = null;
  private monitoring = false;

  constructor() {
    if (typeof window === 'undefined') {
      return;
    }

    this.auth.isAuthenticated$.pipe(filter((ok) => ok)).subscribe(() => {
      this.startMonitoring();
      this.resetInactivityTimers();
    });

    this.auth.isAuthenticated$.pipe(filter((ok) => !ok)).subscribe(() => {
      this.stopMonitoring();
    });

    if (typeof window !== 'undefined') {
      window.addEventListener(VAULT_SECURITY_SETTINGS_UPDATED_EVENT, () => {
        if (this.auth.isAuthenticated()) {
          this.resetInactivityTimers();
        }
      });
    }
  }

  private getInactivityMs(): number | null {
    const fromSettings = readVaultSessionInactivityMinutes();
    const minutes =
      fromSettings ??
      (this.config.sessionInactivityMinutes ?? DEFAULT_INACTIVITY_MINUTES);
    if (minutes <= 0) {
      return null;
    }
    return minutes * 60 * 1000;
  }

  private getWarningMs(inactivityMs: number): number {
    const warnMinutes = this.config.sessionWarningMinutes ?? DEFAULT_WARNING_MINUTES;
    return Math.min(warnMinutes * 60 * 1000, Math.max(inactivityMs - 60_000, 0));
  }

  private isUploadActive(): boolean {
    return localStorage.getItem(UPLOAD_ACTIVE_KEY) === 'true';
  }

  private startMonitoring(): void {
    if (this.monitoring) {
      return;
    }
    this.monitoring = true;

    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    const onActivity = () => this.onUserActivity();

    for (const event of events) {
      document.addEventListener(event, onActivity, true);
    }

    this.uploadCheckInterval = setInterval(() => {
      if (this.isUploadActive()) {
        this.resetInactivityTimers();
      }
    }, 30_000);

    this.onUserActivity();
  }

  private stopMonitoring(): void {
    this.monitoring = false;
    this.clearTimers();
    if (this.uploadCheckInterval) {
      clearInterval(this.uploadCheckInterval);
      this.uploadCheckInterval = null;
    }
  }

  private onUserActivity(): void {
    if (!this.auth.isAuthenticated()) {
      return;
    }

    localStorage.setItem('last_activity_time', String(Date.now()));
    this.auth.noteUserActivity();
    this.resetInactivityTimers();
  }

  private resetInactivityTimers(): void {
    this.clearTimers();

    const inactivityMs = this.getInactivityMs();
    if (inactivityMs === null) {
      return;
    }

    const effectiveTimeout = this.isUploadActive()
      ? Math.max(inactivityMs, UPLOAD_ACTIVE_TIMEOUT_MS)
      : inactivityMs;

    const warningDelay = Math.max(effectiveTimeout - this.getWarningMs(effectiveTimeout), 0);

    this.warningTimeout = setTimeout(() => {
      if (this.isUploadActive()) {
        this.resetInactivityTimers();
        return;
      }

      const remainingMin = Math.max(
        1,
        Math.ceil((effectiveTimeout - warningDelay) / 60_000),
      );

      this.vaultAppToast.show({
        type: 'warning',
        title: 'Sesi?n por expirar',
        message: `Tu sesi?n se cerrar? por inactividad en aproximadamente ${remainingMin} minuto(s). Realiza una acci?n en el sistema para mantenerla activa.`,
        duration: 12_000,
      });

      if (typeof window !== 'undefined') {
        this.dialogService
          .confirm({
            title: 'Sesi?n por expirar',
            message: `Tu sesi?n expirar? por inactividad en ${remainingMin} minuto(s). ?Deseas continuar trabajando?`,
            confirmLabel: 'Continuar',
            cancelLabel: 'Cerrar sesi?n',
            variant: 'warning',
          })
          .pipe(take(1))
          .subscribe((confirmed) => {
            if (confirmed) {
              this.resetInactivityTimers();
            }
          });
      }
    }, warningDelay);

    this.logoutTimeout = setTimeout(() => {
      if (this.isUploadActive()) {
        this.resetInactivityTimers();
        return;
      }

      this.vaultAppToast.show({
        type: 'warning',
        title: 'Sesi?n expirada',
        message: 'Tu sesi?n se cerr? por inactividad. Inicia sesi?n nuevamente.',
        duration: 8000,
      });
      sessionStorage.setItem('session_expired_shown', 'true');
      captureVaultSessionForExpiryLogout();
      this.auth.logout();
    }, effectiveTimeout);
  }

  private clearTimers(): void {
    if (this.logoutTimeout) {
      clearTimeout(this.logoutTimeout);
      this.logoutTimeout = null;
    }
    if (this.warningTimeout) {
      clearTimeout(this.warningTimeout);
      this.warningTimeout = null;
    }
  }
}
